/**
 * burrowRanks.js — SINGLE source of truth for the two member ladders.
 * Unifies Dashboard (DashboardDen.jsx), Feed profile card (MemberProfileCard.jsx),
 * and the Profile drawer (UserDrawer.jsx). Owner decisions 9-7-26.
 *
 * 1) LEVEL ladder  — rank comes from Burrow Points BRACKETS (NOT accountLevel).
 *    Member 0 · Skilled 1,000 · Awake 1,500 · Knowing 3,000 · Dedicated 5,000 · Veteran 10,000.
 * 2) RESPECT ladder — respect is a HEADCOUNT of people who respect you (each +1, take-away −1).
 *    Neutral 0 · Known 1 · Valued 2 · Celebrated 3 · Revered 4 · Legendary 5.
 */

export const LEVEL_RANKS = [
  { name: 'Member',    icon: '🐣', threshold: 0 },
  { name: 'Skilled',   icon: '🛠️', threshold: 1000 },
  { name: 'Awake',     icon: '💡', threshold: 1500 },
  { name: 'Knowing',   icon: '📖', threshold: 3000 },
  { name: 'Dedicated', icon: '🛡️', threshold: 5000 },
  { name: 'Veteran',   icon: '🏆', threshold: 10000 },
];

export const RESPECT_RANKS = [
  { name: 'Neutral',    icon: '🥷', threshold: 0 },
  { name: 'Known',      icon: '🤝', threshold: 1 },
  { name: 'Valued',     icon: '🌟', threshold: 2 },
  { name: 'Celebrated', icon: '🔥', threshold: 3 },
  { name: 'Revered',    icon: '❤️', threshold: 4 },
  { name: 'Legendary',  icon: '👑', threshold: 5 },
];

/** Highest rank whose threshold the value meets (value = burrowPoints for LEVEL, respectCount for RESPECT). */
export const computeRank = (value, ranks) => {
  let idx = 0;
  for (let i = ranks.length - 1; i >= 0; i--) {
    if (value >= ranks[i].threshold) { idx = i; break; }
  }
  const rank = ranks[idx];
  const next = idx < ranks.length - 1 ? ranks[idx + 1] : null;
  const pct = next
    ? Math.min(100, Math.max(0, ((value - rank.threshold) / (next.threshold - rank.threshold)) * 100))
    : 100;
  return { rank, index: idx, next, pct };
};

/** Real burrowPoints for a member payload — or null when the payload has none.
 *  We NEVER invent a point total (owner: no more fake "10,000" in the feed). */
export const memberBpValue = (m) => {
  if (m && m.burrowPoints != null && m.burrowPoints !== '') {
    const v = Number(m.burrowPoints);
    return Number.isFinite(v) ? v : null;
  }
  return null;
};

/** Legacy accountLevel → LEVEL ladder index (old thresholds 1/5/10/20/35/50).
 *  Only used for RANK NAME when a payload lacks real burrowPoints. */
export const legacyLevelIndex = (m) => {
  const al = Number((m && (m.accountLevel ?? m.level)) || 1);
  if (al >= 50) return 5;
  if (al >= 35) return 4;
  if (al >= 20) return 3;
  if (al >= 10) return 2;
  if (al >= 5) return 1;
  return 0;
};

/** Level rank for a member payload: real rank from BP when known, otherwise the
 *  legacy-named rank (no point total is fabricated). known=false ⇒ show '—' BP. */
export const memberLevelRank = (m) => {
  const bp = memberBpValue(m);
  if (bp !== null) return { ...computeRank(bp, LEVEL_RANKS).rank, known: true };
  return { ...LEVEL_RANKS[legacyLevelIndex(m)], known: false };
};
